/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import DiscussionPost from 'src/components/thread-detail/DiscussionPost';
import { fetchReplies, selectPostById, selectFilter } from 'src/redux/slices/threadDetailSlice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { KatSpinner, KatAlert, KatDivider } from '@amzn/katal-react';
import { AppState } from 'src/redux/store';
import { useTranslation } from 'src/utils';
import { DiscussionPostErrors } from 'src/constants/errorStrings';
import { PostErrors, CursorDirection } from 'src/types/DiscussionPost';
import { QuoteRef } from 'src/types/Quotes';

interface RepliesContainerProps {
  threadId: string;
  postId: string;
  postRefs: Record<string, HTMLElement>;
  quoteRef?: QuoteRef;
}

const RepliesContainer: React.FC<RepliesContainerProps> = ((
  {
    threadId, postId, postRefs, quoteRef,
  },
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    childrenPostIds,
    errors,
    isNextChildrenPostsLoading,
    isPreviousChildrenPostsLoading,
    previousCursor,
    nextCursor,
    showReplies,
  } = useAppSelector((state: AppState) => selectPostById(state, postId));
  const filterBy = useAppSelector(selectFilter);
  const fetchNext = () => dispatch(fetchReplies({
    threadId,
    postId,
    cursor: nextCursor,
    direction: CursorDirection.next,
    filterBy,
  }));

  const fetchPrevious = () => dispatch(fetchReplies({
    threadId,
    postId,
    cursor: previousCursor,
    direction: CursorDirection.previous,
    filterBy,
  }));

  useEffect(() => {
    if (childrenPostIds.length === 0 && showReplies) {
      dispatch(fetchReplies({ threadId, postId, filterBy }));
    }
  }, [showReplies]);
  if (!showReplies) return <div data-testid="empty-replies-container" />;

  const getHeader = (): JSX.Element | null => {
    if (errors.includes(PostErrors.fetchPreviousReplies)) {
      return (
        <KatAlert
          data-testid="previous-replies-failure-msg"
          persistent
          variant="warning"
          header={t(DiscussionPostErrors.fetchPreviousReplies.header)}
          description={t(DiscussionPostErrors.fetchPreviousReplies.description)}
        />
      );
    }

    if (isPreviousChildrenPostsLoading) {
      return (<KatSpinner data-testid="load-previous-spinner" className="m-4" size="small" />
      );
    }

    if (previousCursor) {
      return (
        <div className="text-center">
          <button
            type="button"
            data-testid="load-previous-replies"
            onClick={fetchPrevious}
            className="my-7 cursor-pointer border-0 bg-transparent font-medium text-kat-eastern"
          >
            {t('discussionCard_showPreviousPosts')}
          </button>
        </div>
      );
    }
    return null;
  };

  const getFooter = (): JSX.Element | null => {
    if (errors.includes(PostErrors.fetchNextReplies)) {
      return (
        <KatAlert
          data-testid="next-replies-failure-msg"
          persistent
          variant="warning"
          header={t(DiscussionPostErrors.fetchNextReplies.header)}
          description={t(DiscussionPostErrors.fetchNextReplies.description)}
        />
      );
    }

    if (isNextChildrenPostsLoading) {
      return <KatSpinner data-testid="load-more-spinner" className="m-4" size="small" />;
    }

    if (nextCursor) {
      return (
        <div className="text-center">
          <button
            type="button"
            data-testid="load-more-replies"
            onClick={fetchNext}
            className="my-7 cursor-pointer border-0 bg-transparent font-medium text-kat-eastern"
          >
            {t('discussionCard_showMorePosts')}
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="replies-container" className="ltr:ml-[5.9rem] rtl:mr-[5.9rem]">
      {getHeader()}
      {childrenPostIds && childrenPostIds.map((id: string) => (
        <div
          data-testid="reply-post"
          key={id}
          ref={(element: HTMLDivElement) => {
            // eslint-disable-next-line no-param-reassign
            postRefs[id] = element;
          }}
        >
          <DiscussionPost quoteRef={quoteRef} postId={id} />
          <KatDivider variant="athens" />
        </div>
      ))}
      {getFooter()}
    </div>
  );
});
export default RepliesContainer;
